import React, { useEffect } from 'react';
import { Box, Grid, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';

// Import our common form layout components
import FormSelect, { FormInput, FormDatepicker, FormMultiSelect } from './FormControls';
import SupplierField from './SupplierField';

const Accomodation = ({ values }) => {
  // Read parent controls seamlessly through Context
  const { watch, setValue } = useFormContext();

  // Watch values to dynamically compute total days stayed and net value
  const bookInDate = watch('bookInDate');
  const checkoutDate = watch('checkoutDate');
  const totalDays = watch('totalDays');
  const ratePerNight = watch('ratePerNight');

  // Calculate Total Days (Integer)
  useEffect(() => {
    if (bookInDate && checkoutDate) {
      const days = dayjs(checkoutDate).diff(dayjs(bookInDate), 'day');
      // Ensure integer format and prevent negative balances
      const integerDays = days > 0 ? Math.floor(days) : 0;
      setValue('totalDays', String(integerDays));
    } else {
      setValue('totalDays', '0');
    }
  }, [bookInDate, checkoutDate, setValue]);

  // Calculate Total Net Value (Integer)
  useEffect(() => {
    const daysNum = parseInt(totalDays, 10) || 0;
    const rateNum = parseFloat(ratePerNight) || 0;

    if (daysNum > 0 && rateNum > 0) {
      const netValue = Math.floor(daysNum * rateNum);
      setValue('totalNetValue', String(netValue));
    } else {
      setValue('totalNetValue', '0');
    }
  }, [totalDays, ratePerNight, setValue]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* ─── BASIC INFO SECTION ─── */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            Basic Info
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormDatepicker name="poDate" label="Date" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput name="requestorEmail" label="Requestor Email" placeholder="Enter Email" />
            </Grid>
            <SupplierField supplierList={values.supplierList || []} />
            <Grid item xs={12} sm={4}>
              <FormInput name="emailToSupplier" label="Supplier Email" placeholder="Enter Supplier Email" />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ─── BOOKING INFO SECTION ─── */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            Booking Info
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormDatepicker name="bookInDate" label="Book In Date" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput name="emailAddresses" label="Email Address For Email" placeholder="Enter Email" />
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* Pass bookInDate down to enforce basic min-date HTML boundary attributes if desired */}
              <FormDatepicker name="checkoutDate" label="Check Out Date" inputProps={{ min: bookInDate }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput name="totalDays" label="Total Days" placeholder="Enter Days" type="number" disabled />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput
                name="ratePerNight"
                label="Rate Per Night"
                placeholder="Enter Rate"
                type="number"
                inputProps={{ step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput name="totalNetValue" label="Total Net Value" placeholder="Total Value" type="number" disabled />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormInput
                name="description"
                label="Description"
                placeholder="Enter description"
                multiline
                minRows={4}
                maxRows={10}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ─── WARRANTY DETAILS SECTION ─── */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            Warranty Details
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormSelect name="warrantyPeriod" label="Warranty Period" options={values['WarrantyPeriod']} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput name="whPartRefNumber" label="WH Part Ref Number" placeholder="Enter WH Part Ref" type="number" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput
                name="supplierPartRefNumber"
                label="Supplier Part Ref Number"
                placeholder="Enter Supplier Part Ref"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default Accomodation;
